import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import {
  Form,
  Input,
  Select,
  Switch,
  Card,
  Row,
  Col,
  DatePicker,
  Divider,
  Typography,
} from 'antd';
import style from '../../Styles/AddUser.module.css';

dayjs.extend(customParseFormat);
const { TextArea } = Input;
const { Option } = Select;
const { Title } = Typography;

const BasicInformation = ({
  title,
  setTitle,
  description,
  setDescription,
  periodId,
  setPeriodId,
  periodList,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  anonymousResponse,
  setAnonymousResponse,
  allowResubmission,
  setAllowResubmission,
  requireAllQuestions,
  setRequireAllQuestions,
  multiLanguage,
  setMultiLanguage,
  autoSend,
  setAutoSend,
  scheduleDate,
  setScheduleDate,
  departmentIds,
  setDepartmentIds,
  userIds,
  setUserIds,
  departmentList,
  userList,
  errors = {},
  setErrors,
  isPreview = false,
}) => {
  const [periodRange, setPeriodRange] = useState({ start: null, end: null });

  const handleClearError = (field) => {
    if (setErrors && errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Helper to safely parse any incoming date format
  const parseDateSafely = (dateVal) => {
    if (!dateVal) return null;
    if (dayjs.isDayjs(dateVal)) {
      return dateVal.isValid() ? dateVal : null;
    }
    // Try standard and common formats
    const parsed = dayjs(
      dateVal,
      ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD-MM-YYYY', 'MM/DD/YYYY', 'YYYY/MM/DD'],
      true,
    );
    if (parsed.isValid()) return parsed;

    // Fallback to default parse
    const fallback = dayjs(dateVal);
    return fallback.isValid() ? fallback : null;
  };

  // Parse a period date string – supports 'DD-MMMM-YYYY' (e.g. "23-March-2026")
  // as well as standard formats returned by the API
  const parsePeriodDate = (val) => {
    if (!val) return null;
    // Primary: 'DD-MMMM-YYYY' used by the SurveyPeriod form
    const v1 = dayjs(val, 'DD-MMMM-YYYY', true);
    if (v1.isValid()) return v1;
    // Fallback: ISO / common formats
    const v2 = dayjs(val, ['YYYY-MM-DD', 'DD-MM-YYYY', 'DD/MM/YYYY'], true);
    if (v2.isValid()) return v2;
    const v3 = dayjs(val);
    return v3.isValid() ? v3 : null;
  };

  // Get period date range when period changes
  useEffect(() => {
    if (periodId && periodList) {
      const selectedPeriod = periodList.find((p) => p.id === periodId);
      if (selectedPeriod) {
        // fromMonth / toMonth are the fields saved by SurveyPeriod (DD-MMMM-YYYY)
        // fall back to start_date / end_date if those aren't present
        const start = parsePeriodDate(
          selectedPeriod.fromMonth ||
          selectedPeriod.start_date ||
          selectedPeriod.period_start
        );
        const end = parsePeriodDate(
          selectedPeriod.toMonth ||
          selectedPeriod.end_date ||
          selectedPeriod.period_end
        );

        if (start && end) {
          setPeriodRange({ start, end });
        } else {
          setPeriodRange({ start: null, end: null });
        }
      }
    } else {
      setPeriodRange({ start: null, end: null });
    }
  }, [periodId, periodList]);

const disabledStartDate = (current) => {
  if (!current) return false;

  if (periodRange.start && periodRange.end) {
    return (
      current.isBefore(periodRange.start, 'day') ||
      current.isAfter(periodRange.end, 'day')
    );
  }

  return false;
};
const disabledEndDate = (current) => {
  if (!current) return false;

  if (periodRange.start && periodRange.end) {
    return (
      current.isBefore(periodRange.start, 'day') ||
      current.isAfter(periodRange.end, 'day')
    );
  }

  return false;
};

  // Handle start date change with period validation
  const handleStartDateChange = (date) => {
    if (!isPreview) {
      // Validate against period range
      if (periodRange.start && periodRange.end) {
        if (
          date &&
          (date.isBefore(periodRange.start, 'day') ||
            date.isAfter(periodRange.end, 'day'))
        ) {
          // Optionally show error message
          console.warn(
            `Start date must be between ${periodRange.start.format('DD/MM/YYYY')} and ${periodRange.end.format('DD/MM/YYYY')}`,
          );
          return;
        }
      }
      setStartDate(date);

      // If end date exists and is before new start date, clear or adjust it
      if (endDate && date) {
        const currentEnd = parseDateSafely(endDate);
        if (currentEnd && currentEnd.isBefore(date, 'day')) {
          setEndDate(null);
        }
      }
    }
  };

  // Handle end date change with period validation
  const handleEndDateChange = (date) => {
    if (!isPreview) {
      // Validate against period range
      if (periodRange.start && periodRange.end) {
        if (
          date &&
          (date.isBefore(periodRange.start, 'day') ||
            date.isAfter(periodRange.end, 'day'))
        ) {
          console.warn(
            `End date must be between ${periodRange.start.format('DD/MM/YYYY')} and ${periodRange.end.format('DD/MM/YYYY')}`,
          );
          return;
        }
      }
      setEndDate(date);
    }
  };

  // Get disabled months for calendar view (optional enhancement)
  const getDisabledMonth = (current) => {
    if (!periodRange.start || !periodRange.end) return false;

    const currentMonth = current.month();
    const currentYear = current.year();
    const startMonth = periodRange.start.month();
    const startYear = periodRange.start.year();
    const endMonth = periodRange.end.month();
    const endYear = periodRange.end.year();

    // Disable months completely outside the period range
    if (currentYear < startYear) return true;
    if (currentYear > endYear) return true;
    if (currentYear === startYear && currentMonth < startMonth) return true;
    if (currentYear === endYear && currentMonth > endMonth) return true;

    return false;
  };

  return (
    <Card style={{ marginBottom: 0, borderRadius: 8 }}>
      <Form layout="vertical">
        <div className={style['title-style']}>Basic Information</div>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={
                <span>
                  Survey Title
                  {!isPreview && (
                    <span style={{ color: 'red', marginLeft: 2 }}>*</span>
                  )}
                </span>
              }
              required={false}
              validateStatus={errors.title ? 'error' : ''}
              help={errors.title}
            >
              <Input
                status={errors.title ? 'error' : ''}
                value={title}
                onChange={(e) => {
                  if (!isPreview) setTitle(e.target.value);
                  handleClearError('title');
                }}
                placeholder="Enter survey name"
                disabled={isPreview}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={
                <span>
                  Survey Period
                  {!isPreview && (
                    <span style={{ color: 'red', marginLeft: 2 }}>*</span>
                  )}
                </span>
              }
              required={false}
              validateStatus={errors.periodId ? 'error' : ''}
              help={errors.periodId}
            >
              <Select
                status={errors.periodId ? 'error' : ''}
                style={{ width: '100%' }}
                placeholder="Select"
                value={periodId || undefined}
                onChange={(val) => {
                  if (!isPreview) {
                    setPeriodId(val);
                    setStartDate(null);
                    setEndDate(null);
                    handleClearError('periodId');
                  }
                }}
                allowClear
                disabled={isPreview}
              >
                {periodList?.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {item.period_name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label={
                <span>
                  Start Date
                  {!isPreview && (
                    <span style={{ color: 'red', marginLeft: 2 }}>*</span>
                  )}
                </span>
              }
              required={false}
              validateStatus={errors.startDate ? 'error' : ''}
              help={errors.startDate}
              // extra={
              //   periodRange.start &&
              //   periodRange.end && (
              //     <span style={{ fontSize: 12, color: '#666' }}>
              //       Available period: {periodRange.start.format('DD/MM/YYYY')} -{' '}
              //       {periodRange.end.format('DD/MM/YYYY')}
              //     </span>
              //   )
              // }
            >
              <DatePicker
                status={errors.startDate ? 'error' : ''}
                style={{ width: '100%' }}
                value={parseDateSafely(startDate)}
                onChange={(date) => {
                   handleStartDateChange(date);
                   handleClearError('startDate');
                }}
                format="DD/MM/YYYY"
                placeholder={periodId ? 'Select start date' : 'Select a period first'}
                disabled={isPreview || !periodId}
                disabledDate={disabledStartDate}
                defaultPickerValue={parseDateSafely(startDate) || periodRange.start || undefined}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={
                <span>
                  End Date
                  {!isPreview && (
                    <span style={{ color: 'red', marginLeft: 2 }}>*</span>
                  )}
                </span>
              }
              required={false}
              validateStatus={errors.endDate ? 'error' : ''}
              help={errors.endDate}
            >
              <DatePicker
                status={errors.endDate ? 'error' : ''}
                style={{ width: '100%' }}
                value={parseDateSafely(endDate)}
                onChange={(date) => {
                  handleEndDateChange(date);
                  handleClearError('endDate');
                }}
                format="DD/MM/YYYY"
                placeholder={periodId ? 'Select end date' : 'Select a period first'}
                disabled={isPreview || !periodId}
                disabledDate={disabledEndDate}
                defaultPickerValue={parseDateSafely(endDate) || periodRange.start || undefined}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Description">
          <TextArea
            rows={3}
            value={description}
            onChange={(e) => !isPreview && setDescription(e.target.value)}
            placeholder="Describe this survey"
            disabled={isPreview}
          />
        </Form.Item>

        <div className={style['title-style']}>Target Audience</div>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Department">
              <Select
                status={errors.departmentIds ? 'error' : ''}
                style={{ width: '100%' }}
                mode="multiple"
                placeholder="Select Department"
                value={departmentIds}
                onChange={(val) => {
                  if (!isPreview) {
                    setDepartmentIds(val);
                    handleClearError('departmentIds');
                  }
                }}
                allowClear
                showSearch
                optionFilterProp="children"
                disabled={isPreview}
              >
                {departmentList.map((item) => (
                  <Option key={item.value} value={item.value}>
                    {item.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label="Users">
              <Select
                style={{ width: '100%' }}
                mode="multiple"
                placeholder="Select Users"
                value={userIds}
                onChange={!isPreview ? setUserIds : undefined}
                disabled={isPreview}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {userList.map((item) => {
                  const userName =
                    item.name ||
                    (item.first_name
                      ? `${item.first_name} ${item.last_name || ''}`.trim()
                      : null) ||
                    item.label ||
                    item.value;
                  return (
                    <Option
                      key={item.id || item.value}
                      value={item.id || item.value}
                    >
                      {userName}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default BasicInformation;
